import { useState } from "react";
import { Loader2, UserPlus, UserMinus, X } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  influencerAPIClient,
  type BulkAddProgress,
} from "@/marketing/infrastructure/influencer/influencerAPIClient";
import { INFLUENCER_KEYS } from "@/marketing/infrastructure/influencer/utils/keys";
import { useListStore } from "@/marketing/stores/useListStore";
import type { TabId, DiscoveryFilters } from "@/marketing/models/influencer";

interface BulkActionBarProps {
  checkedIds: Set<string>;
  onClear: () => void;
  activeTab?: TabId;
  selectAllFilteredMode?: boolean;
  selectAllFilteredCount?: number;
  filters?: DiscoveryFilters;
}

export function BulkActionBar({
  checkedIds,
  onClear,
  activeTab = "discover",
  selectAllFilteredMode = false,
  selectAllFilteredCount = 0,
  filters,
}: BulkActionBarProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const activeListId = useListStore((s) => s.activeListId);
  const queryClient = useQueryClient();

  const displayCount = selectAllFilteredMode
    ? selectAllFilteredCount
    : checkedIds.size;
  if (displayCount === 0) return null;

  const invalidateAll = () => {
    if (activeListId) {
      queryClient.invalidateQueries({
        queryKey: INFLUENCER_KEYS.listMembers(activeListId),
        refetchType: "all",
      });
    }
    queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.lists() });
    queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.all });
  };

  const handleBulkAdd = async () => {
    if (!activeListId) {
      toast.error("No list selected");
      return;
    }

    setIsLoading(true);
    setProgress("");
    try {
      if (selectAllFilteredMode && filters) {
        const result = await influencerAPIClient.addAllFilteredToList(
          activeListId,
          filters,
          (p: BulkAddProgress) => {
            switch (p.phase) {
              case "fetching":
                setProgress(
                  p.total > 0
                    ? `Fetching ${p.processed.toLocaleString()} / ${p.total.toLocaleString()}...`
                    : "Fetching influencers...",
                );
                break;
              case "adding":
                setProgress(
                  `Adding & enriching ${p.processed.toLocaleString()} / ${p.total.toLocaleString()}...`,
                );
                break;
            }
          },
        );

        toast.success(
          `Added ${result.added} influencer(s)${result.duplicates > 0 ? `, ${result.duplicates} already in list` : ""}`,
        );
      } else if (selectAllFilteredMode && !filters) {
        console.warn("selectAllFilteredMode is true but filters not provided");
        toast.error("Cannot add filtered selection without filters");
        setIsLoading(false);
        return;
      } else {
        const result = await influencerAPIClient.addListMembers(activeListId, [
          ...checkedIds,
        ]);
        toast.success(
          `Added ${result.added} influencer(s)${result.duplicates > 0 ? `, ${result.duplicates} already in list` : ""}`,
        );
      }

      invalidateAll();
      onClear();
    } catch (err) {
      console.error("Failed to bulk add:", err);
      toast.error("Failed to add influencers to list");
    } finally {
      setIsLoading(false);
      setProgress("");
    }
  };

  const handleBulkRemove = async () => {
    if (!activeListId) {
      toast.error("No list selected");
      return;
    }

    setIsLoading(true);
    setProgress("");
    try {
      let idsToRemove: string[];
      if (selectAllFilteredMode) {
        const all = await influencerAPIClient.getAllListMembers(
          activeListId,
          (p) => {
            setProgress(
              `Fetching ${p.fetched.toLocaleString()} / ${p.total.toLocaleString()}...`,
            );
          },
        );
        idsToRemove = all.members.map((m) => m.id);
      } else {
        idsToRemove = [...checkedIds];
      }

      const batchSize = 500;
      let totalRemoved = 0;
      for (let i = 0; i < idsToRemove.length; i += batchSize) {
        const batch = idsToRemove.slice(i, i + batchSize);
        const result = await influencerAPIClient.removeListMembers(
          activeListId,
          batch,
        );
        totalRemoved += result.removed;
        setProgress(
          `Removing ${Math.min(i + batchSize, idsToRemove.length).toLocaleString()} / ${idsToRemove.length.toLocaleString()}...`,
        );
      }

      toast.success(`Removed ${totalRemoved} influencer(s) from list`);
      invalidateAll();
      onClear();
    } catch (err) {
      console.error("Failed to bulk remove:", err);
      toast.error("Failed to remove influencers from list");
    } finally {
      setIsLoading(false);
      setProgress("");
    }
  };

  const isSelectedTab = activeTab === "selected";

  return (
    <div
      className={`tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-px-4 tw-py-2 ${
        isSelectedTab
          ? "tw-border-red-200 tw-bg-red-50"
          : "tw-border-blue-200 tw-bg-blue-50"
      }`}
    >
      <span
        className={`tw-text-sm tw-font-medium ${isSelectedTab ? "tw-text-red-900" : "tw-text-blue-900"}`}
      >
        {displayCount.toLocaleString()} selected
        {selectAllFilteredMode && " (all filtered)"}
      </span>
      {progress && <span className="tw-text-xs tw-text-blue-700">{progress}</span>}
      {isSelectedTab ? (
        <button
          onClick={handleBulkRemove}
          disabled={isLoading}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-bg-red-600 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-red-700 disabled:tw-opacity-50"
        >
          {isLoading ? (
            <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
          ) : (
            <UserMinus className="tw-h-3.5 tw-w-3.5" />
          )}
          Remove from List
        </button>
      ) : (
        <button
          onClick={handleBulkAdd}
          disabled={isLoading}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-bg-[#2563EB] tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50"
        >
          {isLoading ? (
            <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
          ) : (
            <UserPlus className="tw-h-3.5 tw-w-3.5" />
          )}
          Add to List
        </button>
      )}
      <button
        onClick={onClear}
        className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-px-2 tw-py-1.5 tw-text-sm ${
          isSelectedTab
            ? "tw-text-red-700 hover:tw-bg-red-100"
            : "tw-text-blue-700 hover:tw-bg-blue-100"
        }`}
      >
        <X className="tw-h-3.5 tw-w-3.5" />
        Cancel
      </button>
    </div>
  );
}
